import type { StemManifest } from "../types";

type MasterBus = NonNullable<StemManifest["masterBus"]>;

function setStereoWidth(
  context: AudioContext,
  input: AudioNode,
  width: number,
): AudioNode {
  const splitter = context.createChannelSplitter(2);
  const merger = context.createChannelMerger(2);
  const direct = (1 + width) * 0.5;
  const cross = (1 - width) * 0.5;
  const matrix = [
    { source: 0, destination: 0, gain: direct },
    { source: 1, destination: 0, gain: cross },
    { source: 0, destination: 1, gain: cross },
    { source: 1, destination: 1, gain: direct },
  ];
  input.connect(splitter);
  for (const route of matrix) {
    const gain = context.createGain();
    gain.gain.value = route.gain;
    splitter.connect(gain, route.source);
    gain.connect(merger, 0, route.destination);
  }
  return merger;
}

export function createMasterInput(
  context: AudioContext,
  bus?: MasterBus,
): GainNode {
  const master = context.createGain();
  if (!bus) {
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -10;
    compressor.knee.value = 1;
    compressor.ratio.value = 10;
    compressor.attack.value = 0.002;
    compressor.release.value = 0.08;
    master.connect(compressor).connect(context.destination);
    return master;
  }

  let tail: AudioNode = master;
  for (const spec of bus.filters) {
    const filter = context.createBiquadFilter();
    filter.type = spec.type === "highshelf" ? "highshelf" : "peaking";
    filter.frequency.value = spec.frequencyHz;
    filter.Q.value = spec.q;
    filter.gain.value = spec.gainDb;
    tail.connect(filter);
    tail = filter;
  }
  const preGain = context.createGain();
  preGain.gain.value = 10 ** (bus.preGainDb / 20);
  tail.connect(preGain);
  const compressor = context.createDynamicsCompressor();
  compressor.threshold.value = bus.compressor.thresholdDb;
  compressor.knee.value = bus.compressor.kneeDb;
  compressor.ratio.value = bus.compressor.ratio;
  compressor.attack.value = bus.compressor.attackSeconds;
  compressor.release.value = bus.compressor.releaseSeconds;
  preGain.connect(compressor);

  let postDynamics: AudioNode = compressor;
  if (bus.saturation) {
    const saturator = context.createWaveShaper();
    const points = 65_537;
    const curve = new Float32Array(points);
    const normalization = Math.tanh(bus.saturation.drive);
    for (let index = 0; index < points; index += 1) {
      const input = index * 2 / (points - 1) - 1;
      curve[index] = Math.tanh(bus.saturation.drive * input) / normalization;
    }
    saturator.curve = curve;
    saturator.oversample = "4x";
    const dry = context.createGain();
    const wet = context.createGain();
    const blend = context.createGain();
    dry.gain.value = 1 - bus.saturation.mix;
    wet.gain.value = bus.saturation.mix;
    compressor.connect(dry).connect(blend);
    compressor.connect(saturator).connect(wet).connect(blend);
    postDynamics = blend;
  }

  const postWidth = bus.stereoWidth === undefined
    ? postDynamics
    : setStereoWidth(context, postDynamics, bus.stereoWidth);
  const limiter = context.createDynamicsCompressor();
  limiter.threshold.value = bus.limiter.browserThresholdDb;
  limiter.knee.value = 0;
  limiter.ratio.value = bus.limiter.browserRatio;
  limiter.attack.value = bus.limiter.attackSeconds;
  limiter.release.value = bus.limiter.releaseSeconds;
  postWidth.connect(limiter);
  if (bus.browserOutputTrimDb === undefined) {
    limiter.connect(context.destination);
  } else {
    const outputTrim = context.createGain();
    outputTrim.gain.value = 10 ** (bus.browserOutputTrimDb / 20);
    limiter.connect(outputTrim).connect(context.destination);
  }
  return master;
}
